import { apiClient } from '@/utils/api-client'

const API_INTERNAL_BASE = '/projects'

function hasProjectKey(projectKey) {
    return projectKey !== undefined && projectKey !== null && projectKey !== ''
}

/**
 * Feature flag service for managing flags within projects.
 *
 * Provides CRUD operations for feature flags.
 *
 * All methods require a valid projectKey. Missing or invalid keys will result
 * in rejected promises to make errors explicit rather than silently returning
 * empty data. Components should validate projectKey before calling service methods.
 */
class FlagService {
    /**
     * Create a new feature flag.
     *
     * @param {string} projectKey - Project key (required)
     * @param {Object} form - Flag data
     * @param {string} form.key - Unique flag key
     * @param {string} form.name - Display name
     * @param {string} form.type - Flag type (boolean, string, number, json)
     * @returns {Promise<Object>} Created flag data
     * @throws {Error} Error object if creation fails
     */
    async create(projectKey, form) {
        if (!hasProjectKey(projectKey)) {
            return Promise.reject(new Error('Missing project key.'))
        }

        return await apiClient
            .post(API_INTERNAL_BASE + `/${projectKey}/flags`, form)
            .then((response) => {
                return response.data
            })
    }

    /**
     * Delete a feature flag.
     *
     * @param {string} projectKey - Project key (required)
     * @param {string} flagKey - Flag key to delete
     * @returns {Promise<Object>} Deletion confirmation
     * @throws {Error} Error object if deletion fails
     */
    async remove(projectKey, flagKey) {
        if (!hasProjectKey(projectKey)) {
            return Promise.reject(new Error('Missing project key.'))
        }

        return await apiClient
            .delete(API_INTERNAL_BASE + `/${projectKey}/flags/${flagKey}`)
            .then((response) => {
                return response.data
            })
    }

    /**
     * Archive/delete multiple feature flags by key in a single request.
     *
     * @param {string} projectKey - Project key (required)
     * @param {Array<string>} flagKeys - Flag keys to delete
     * @returns {Promise<Object>} Bulk deletion result with `deleted` and `not_found` key lists
     * @throws {Error} Error object if the request fails
     */
    async bulkRemove(projectKey, flagKeys) {
        if (!hasProjectKey(projectKey)) {
            return Promise.reject(new Error('Missing project key.'))
        }

        return await apiClient
            .post(API_INTERNAL_BASE + `/${projectKey}/flags/bulk-delete`, {
                flag_keys: flagKeys,
            })
            .then((response) => {
                return response.data
            })
    }

    /**
     * Get a single feature flag by key.
     *
     * @param {string} projectKey - Project key (required)
     * @param {string} flagKey - Flag key to retrieve
     * @returns {Promise<Object>} Flag data with values and targets
     * @throws {Error} Error object if flag not found or projectKey is missing
     */
    async get(projectKey, flagKey) {
        if (!hasProjectKey(projectKey)) {
            return Promise.reject(new Error('Missing project key.'))
        }

        return await apiClient
            .get(API_INTERNAL_BASE + `/${projectKey}/flags/${flagKey}`)
            .then((response) => {
                return response.data
            })
    }

    /**
     * Get all feature flags for a project.
     *
     * @param {string} projectKey - Project key (required)
     * @returns {Promise<Array>} Array of flag objects
     * @throws {Error} Error object if request fails or projectKey is missing
     */
    async list(projectKey) {
        if (!hasProjectKey(projectKey)) {
            return Promise.reject(new Error('Missing project key.'))
        }

        return await apiClient
            .get(API_INTERNAL_BASE + `/${projectKey}/flags`)
            .then((response) => {
                return response.data
            })
    }

    /**
     * Update an existing feature flag.
     *
     * @param {string} projectKey - Project key (required)
     * @param {string} flagKey - Flag key to update
     * @param {Object} form - Updated flag data
     * @returns {Promise<Object>} Updated flag data
     * @throws {Error} Error object if update fails
     */
    async update(projectKey, flagKey, form) {
        if (!hasProjectKey(projectKey)) {
            return Promise.reject(new Error('Missing project key.'))
        }

        return await apiClient
            .put(API_INTERNAL_BASE + `/${projectKey}/flags/${flagKey}`, form)
            .then((response) => {
                return response.data
            })
    }
}

export default new FlagService()
